import { evaluatePerformanceORA } from '../../../service/service';
import { getMetricsWithFallback } from '../../../helpers/progressFallback';
import LogProcess from "../../StructuredLog/LogProcess";
import { sendDataProgressTrackerMetrics } from "./VRFactory";

const WizSpeechVRFactory = async (audioBlob, data, userInfo, lessonName, recordingAttempts, engagementTime, tenant) => {
  const text = data.en; // Phrase from the lesson

  const handleScoreDataWizSpeech = async (audioBlob, text) => {
    try {
      // Log the process start
      await LogProcess("success", `Sending audio data for scoring - WizSpeech`);

      // Prepare the FormData for sending to ORA (WizSpeech)
      const formData = new FormData();
      formData.append('recording', audioBlob, 'audio.wav');
      formData.append('referenceText', text);
      formData.append('vendor', 'ORA');  // Indicate vendor as ORA/Google

      // Call the ORA API (evaluatePerformanceORA is an assumed service function)
      const response = await evaluatePerformanceORA(formData, tenant);

      await LogProcess("success", `At handleScoreDataWizSpeech the received scoring data from
         evaluatePerformanceORA response is ${JSON.stringify(response)}`);

      const { data: metrics } = response;

      // Check if the status code from ORA (WizSpeech) is successful (e.g., 200)
      if (response.status === 200) {

        await LogProcess("success", `at handleScoreDataWizSpeech Received 
          response: ${JSON.stringify(response)}`);

        return { success: true, data: response };
      } else {
        // Log the error if ORA didn't return a success status
        await LogProcess("error", `At handleScoreDataWizSpeech returned an error: ${JSON.stringify(metrics)}`);
        return { success: false, error: metrics };
      }
    } catch (error) {

      await LogProcess("error", `Error at handleScoreDataWizSpeech in WizSpeech API: ${JSON.stringify(error)}`);
      return { success: false, error: error };
    }
  };

  return handleScoreDataWizSpeech(audioBlob, text);
};

const getResponseStatus = async (res) => {

  await LogProcess(`success`, `Starting processment to method getResponseStatus`);

  const hasSuccessfulStatus = res && res.status >= 200 && res.status <= 299;
  const hasScores = res && res.data && Array.isArray(res.data.scores);

  if (hasSuccessfulStatus || hasScores) {
    await LogProcess("success", `Processment result success at method getResponseStatus is ${JSON.stringify(res)}`);
    return 'success';
  }

  if (res && res.response && res.response.status === 404) {
    await LogProcess("warn", `Processment result not_found at method getResponseStatus is ${JSON.stringify(res)}`);
    return 'not_found';
  }

  await LogProcess("error", `Processment result error at method getResponseStatus is ${JSON.stringify(res)}`);
  return 'error';
}

const fetchWizSpeechVRData = async (blockData, currentPhrase, tenant) => {
  try {
    await LogProcess("success", `At fetchWizSpeechVRData is starting working`);

    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const res = await getMetricsWithFallback(userInfo.id, blockData.block_id, tenant);

    // notFound is the consolidated /v2/progress signal for "no metrics record yet" (replaces the old 404 branch below).
    if (res && res.data && res.data.notFound) {
      await LogProcess("success", "No previous attempts found for user/block (consolidated notFound).");
      return {
        success: true,
        data: { data: { status_code: 200, attempts: 0 } },
        message: 'No previous attempts found'
      };
    }

    const status = await getResponseStatus(res);

    if (status === 'success' && res.data && res.data.scores && Array.isArray(res.data.scores)) {
      // Verifica se há pontuações antes de filtrar
      if (res && res.data && res.data.scores && res.data.scores.length > 0) {

        // Log para verificar os dados scores
        await LogProcess("success", `at fetchWizSpeechVRData success to fetch data: ${JSON.stringify(res.data.scores)}`);

        // Filtra os dados de acordo com a phrase igual ao currentPhrase
        // const filteredScores = res.data.scores.filter(score => score && score.phrase 
        //   && typeof score.phrase === 'string' && score.phrase.trim() === currentPhrase.trim());

        // Filtra os dados de acordo com a phrase igual ao currentPhrase
        let filteredScores = [];
        if (Array.isArray(res.data.scores) && res.data.scores.length > 0) {
          filteredScores = res.data.scores.filter(score => {
            if (score && score.phrase && typeof score.phrase === 'string') {
              // Verifica se a frase do score corresponde à frase atual
              return score.phrase.trim() === currentPhrase.trim();
            }
            return false;
          });
        }

        // Check of is an array empty filteredScores array
        if (filteredScores.length === 0) {
          // Trata o caso onde não há score para a frase atual
          await LogProcess("success", "No score was found for current phrase.");

          return {
            success: true,
            data: {
              data: {
                status_code: 200,
                attempts: 0
              }
            }, message: 'No previous attempts found'
          };
        }

        // Log para verificar os dados filtrados
        await LogProcess("success", `Filtered scores: ${JSON.stringify(filteredScores)}`);

        // It's Getting the last score accord as needed.
        const standardizedData = standardizeWizSpeechVRData({ data: { ...res.data, scores: filteredScores } });

        await LogProcess("success", `####### Standardized data to return: ${JSON.stringify(standardizedData)}`);
        return { success: true, data: standardizedData };

      } else {
        await LogProcess("warn", `No scores available in the response: ${JSON.stringify(res)}`);
        return { success: false, message: 'No scores available' };
      }

    }

    return { success: false, message: 'No scores available or error fetching attempts' };

  } catch (error) {

    //{"error":"Could not find user with provided \"userId\" and \"blockId\""}
    if (error.message == 'Request failed with status code 404') {
      return {
        success: true,
        data: {
          data: {
            status_code: 200,
            attempts: 0
          }
        }, message: 'No previous attempts found'
      };
    }

    const message = error.message || "";
    await LogProcess("error", `Error at fetchWizSpeechVRData fetching metrics: ${JSON.stringify(message)}`);
    return { success: false, error };
  }
};

const standardizeWizSpeechVRData = (wizMetrics) => {
  try {
    // Suponha que você queira pegar o último score ou o primeiro. Ajuste conforme necessário
    const latestScore = wizMetrics.data != null ?
      wizMetrics.data.scores[wizMetrics.data.scores.length - 1] : 0;

    return {
      data: {
        status_code: 200,
        attempts: wizMetrics.data.scores.length,
        result: {
          overall_score: latestScore.attempt_score,
          overall_metrics: {
            ielts_score: null,
            ielts_fluency: null,
            word_count: latestScore.phrase.split(" ").length, // Contagem das palavras na frase
            syllable_count: null,
            phone_count: null,
          },
          detailed: {
            words: latestScore.phrase.split(" ").map(word => ({
              word: word,
              quality_score: latestScore.attempt_score, // Ajuste conforme necessário
              start: 0,  // Placeholder, ajuste conforme a lógica
              stop: 0    // Placeholder, ajuste conforme a lógica
            })),
            syllables: [],
            phonemes: []
          }
        },
        text: latestScore.phrase,
        audio_duration_seconds: wizMetrics.audio_duration || 0
      }
    };
  } catch (error) {
    throw new Error(error || "Ops! An Error occurs at standardizeWizSpeechMetrics");
  }
};


const standardizeWizSpeechMetrics = (wizMetrics, data) => {
  return {
    data: {
      status_code: 200,
      result: {
        overall_score: wizMetrics.data.overall_score,
        overall_metrics: {
          // If WizSpeech doesn't provide specific metrics, use placeholders or defaults
          ielts_score: null,
          ielts_fluency: null,
          word_count: wizMetrics.data.wordsAccuracy.length,
          syllable_count: null, // If not available
          phone_count: null,    // If not available
        },
        detailed: {
          words: wizMetrics.data.wordsAccuracy.map(word => ({
            word: word.word,
            quality_score: word.accuracyScore,
            start: 0,  // Placeholder if WizSpeech doesn’t return start/stop
            stop: 0    // Placeholder if WizSpeech doesn’t return start/stop
          })),
          syllables: [],  // Populate if WizSpeech provides syllables info
          phonemes: []    // Populate if WizSpeech provides phoneme info
        }
      },
      text: data.en,
      audio_duration_seconds: wizMetrics.audio_duration || 10
    }
  };
};


const handleWizSpeechVRAttemptMetrics = async (response, metrics, userInfo, data, lessonId, lessonName,
  totalIndexes, blockData, recordingAttempts, engagementTime,
  callbackResponseScore, callbackRegisterMetrics) => {

  await LogProcess("success", `At method handleWizSpeechVRAttemptMetrics - Starting handle attempt metrics ${JSON.stringify(metrics)}`);
  if (response.success) {

    // Standardize WizSpeech response to match SpeechAce structure
    const standardizedMetrics = standardizeWizSpeechMetrics(metrics, data);

    // Invoke the callback for updating data to the callback for UI display
    callbackResponseScore(standardizedMetrics);

    // Update the recording attempts count
    const newRecordingAttempts = recordingAttempts + 1;
    const overallScore = standardizedMetrics.data.result.overall_score;

    await sendDataProgressTrackerMetrics(userInfo, lessonId, lessonName, totalIndexes,
      blockData, overallScore, newRecordingAttempts,
      engagementTime, data, callbackRegisterMetrics, "wizspeech");

    return newRecordingAttempts;

  } else {
    await LogProcess("error", `Error: WizSpeech returned a status code different than 200 - ${JSON.stringify(metrics)}`);
    return recordingAttempts; // No increment in attempts
  }
};

export { WizSpeechVRFactory, fetchWizSpeechVRData, handleWizSpeechVRAttemptMetrics }